/* Bisou Rivage — mises en situation de la fiche produit (Standby 3A, 19/10/2026)
 *
 * Chaque diapo « scène » accroche le tirage dans une pièce, à l'échelle (A4 30×40, A3 40×50, A2 50×70).
 * Le cadre accroché EST l'image encadrée du produit, celle de la vue principale (studio de l'App) :
 * même cadre, même passe-partout, même papier, même cadrage de la photo, même titre et signature.
 * Blender ne fournit que la pièce et sa lumière. Quatre calques, du fond vers l'avant :
 *   1. la pièce, sans cadre ;
 *   2. l'ombre du cadre sur le mur (multiply) ;
 *   3. l'image encadrée du produit, posée sur les quatre coins du cadre (matrix3d), rognée à son contour ;
 *   4. la lumière de la pièce sur le cadre (multiply) : il prend la couleur et l'intensité de la pièce.
 *
 * Voie mixte (Max, 20/10 ; fiche parcours-scenes-moteur-mixte) : une scène qui a ses rendus du vrai cadre (« mixte »
 * dans la config, les Vinyles d'abord) remplace le calque 4 par trois calques rendus une fois dans Blender, par cas
 * (format, finition), sur toute la pièce : la lumière reçue dans le cadre (multiply) ; la moulure du vrai cadre en
 * relief, l'emplacement de la photo transparent ; le reflet du verre (screen, dosé par la config). La photo du
 * tirage reste celle du calque 3, dans le trou. Un cas dont un calque manque garde les quatre calques ci-dessus.
 *
 * Le format change la taille du cadre dans la pièce. La finition change la couleur du cadre.
 * « Sans cadre » : seule la vue principale change ; les scènes gardent le dernier cadre choisi
 * (le noir, ou la finition mise en avant du tirage), à la taille du format.
 *
 * Additif : ne touche ni au panier, ni aux variantes, ni à la galerie native. Écoute bisou:variant-change.
 */
(function () {
  'use strict';

  /* ── Homographie : quatre points source -> quatre points destination (CSS matrix3d) ── */
  function adj(m) {
    return [m[4] * m[8] - m[5] * m[7], m[2] * m[7] - m[1] * m[8], m[1] * m[5] - m[2] * m[4],
            m[5] * m[6] - m[3] * m[8], m[0] * m[8] - m[2] * m[6], m[2] * m[3] - m[0] * m[5],
            m[3] * m[7] - m[4] * m[6], m[1] * m[6] - m[0] * m[7], m[0] * m[4] - m[1] * m[3]];
  }
  function mm(a, b) {
    var c = [];
    for (var i = 0; i < 3; i++) for (var j = 0; j < 3; j++) {
      var s = 0;
      for (var k = 0; k < 3; k++) s += a[3 * i + k] * b[3 * k + j];
      c[3 * i + j] = s;
    }
    return c;
  }
  function mv(m, v) {
    return [m[0] * v[0] + m[1] * v[1] + m[2] * v[2], m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
            m[6] * v[0] + m[7] * v[1] + m[8] * v[2]];
  }
  function basis(p) {
    var m = [p[0][0], p[1][0], p[2][0], p[0][1], p[1][1], p[2][1], 1, 1, 1];
    var v = mv(adj(m), [p[3][0], p[3][1], 1]);
    return mm(m, [v[0], 0, 0, 0, v[1], 0, 0, 0, v[2]]);
  }
  function matrix3d(src, dst) {
    var t = mm(basis(dst), adj(basis(src)));
    for (var i = 0; i < 9; i++) t[i] /= t[8];
    return 'matrix3d(' + [t[0], t[3], 0, t[6], t[1], t[4], 0, t[7], 0, 0, 1, 0, t[2], t[5], 0, t[8]].join(',') + ')';
  }

  /* ── Le contour extérieur du cadre dans l'image encadrée du produit ──
     Gabarits du studio de l'App (overlay/full/<format>_cadre_<couleur>.png, 4000×3200 / 3200×4000), les mêmes
     pour les quatre couleurs. Les images en 2500×2000 sont les mêmes gabarits réduits : on travaille en fractions. */
  var CONTOUR = {
    h: { A2: [82 / 4000, 215 / 3200, 3912 / 4000, 2979 / 3200],
         A3: [339 / 4000, 257 / 3200, 3685 / 4000, 2949 / 3200],
         A4: [360 / 4000, 358 / 3200, 3639 / 4000, 2856 / 3200] },
    v: { A2: [215 / 3200, 88 / 4000, 2979 / 3200, 3918 / 4000],
         A3: [257 / 3200, 315 / 4000, 2949 / 3200, 3661 / 4000],
         A4: [358 / 3200, 361 / 4000, 2860 / 3200, 3650 / 4000] }
  };
  /* ── La partie de la pièce que montre la diapo (fiche parcours-scenes-taille, 20/10) ──
     [gauche, haut, zoom] : coin haut gauche de la partie montrée, en fractions de la pièce, et zoom (la partie
     fait 1 / zoom de la pièce, dans les deux sens : même rapport que la diapo). La même pour A4, A3 et A2 : le
     cadre grandit dans la même vue, à la même échelle, et le meuble dessous reste visible pour juger la taille.
     Le plein écran montre toute la pièce. Une scène absente de la table : toute la pièce.
     22/10 (v6-10 ; diagnostic v5, cause 2) : on arrive sur la pièce entière. Le recadrage de 1,3 à 1,6 faisait
     arriver le Buffet en gros plan de lambris, le meuble coupé ; la taille vient de la diapo, pas du recadrage.
     Un zoom d'arrivée ne dépasse jamais 1,1 (le recul part alors de là et finit sur la pièce entière). */
  var CADRAGE = {
    buffet: { h: [0, 0, 1], v: [0, 0, 1] },
    rodalm: { h: [0, 0, 1], v: [0, 0, 1] },
    vinyles: { h: [0, 0, 1], v: [0, 0, 1] }
  };
  var TOUTE_LA_PIECE = [0, 0, 1];
  /* ── Au téléphone, une partie plus serrée de la même pièce (fiche parcours-scenes-rendus-recul-v7, 22/10) ──
     Les rendus du 22/10 sont carrés et vus de plus loin : dans une diapo de 358 px, le cadre A3 n'y ferait que 65 à
     90 px. Sous TELEPHONE_MAX px de diapo, on part de cette partie ([gauche, haut, zoom], comme CADRAGE), centrée sur
     le cadre, pour que l'A3 y fasse au moins 90 px ; pas de recul vers la pièce entière. Le plein écran montre
     toujours toute la pièce. Une scène absente de la table : CADRAGE. */
  var TELEPHONE_MAX = 450;   /* le téléphone (diapo de 358 px) ; l’iPad (512 px, A3 à 128 px) garde la pièce entière */
  var CADRAGE_TELEPHONE = {
    buffet: { h: [0.1, 0, 1.25], v: [0.155, 0, 1.45] },
    vinyles: { h: [0.1, 0, 1.25], v: [0.155, 0, 1.45] },
    /* RÖDALM et Rivage, rendus à leur tour en carré le 23/10 (fiche parcours-scenes-rendus-recul-v7-suite) */
    rodalm: { h: [0.1, 0, 1.25], v: [0.155, 0, 1.45] },
    rivage: { h: [0.1, 0, 1.25], v: [0.155, 0, 1.45] }
  };
  function cadragePour(id, o, largeur) {
    var t = largeur > 0 && largeur < TELEPHONE_MAX && CADRAGE_TELEPHONE[id] && CADRAGE_TELEPHONE[id][o];
    return t || (CADRAGE[id] && CADRAGE[id][o]) || TOUTE_LA_PIECE;
  }
  /* ── La partie montrée, à la forme de la boîte (fiche parcours-scenes-v4-plein-bloc-recul, 21/10) ──
     La diapo peut avoir une autre forme que la pièce (la scène sur tout le bloc gris). On garde la partie choisie
     (son centre et son zoom), on l'étend dans le sens qui manque jusqu'au rapport de la boîte, on la décale pour
     rester dans la pièce, et on ne la rogne que si la pièce est trop petite. La vue devient [gauche, haut, zoom en
     largeur, zoom en hauteur]. Même forme que la pièce (à 1 % près) : la vue d'avant, telle quelle. */
  function vuePour(base, rPiece, rBoite) {
    var k = rBoite / rPiece;
    if (!(k > 0) || Math.abs(k - 1) < 0.01) return [base[0], base[1], base[2], base[2]];
    var z = base[2], lx = 1 / z, ly = 1 / z, cx = base[0] + lx / 2, cy = base[1] + ly / 2;
    if (k > 1) lx = ly * k; else ly = lx / k;
    if (lx > 1) { ly /= lx; lx = 1; }
    if (ly > 1) { lx /= ly; ly = 1; }
    return [Math.min(Math.max(cx - lx / 2, 0), 1 - lx), Math.min(Math.max(cy - ly / 2, 0), 1 - ly), 1 / lx, 1 / ly];
  }
  /* ── Le recul de la caméra (Max, 21/10 : « la caméra recul légerment pour laisser voir plus de pieces ») ──
     Quand la diapo devient visible, une fois : un lent dézoom de la partie choisie vers une vue plus large, pas une
     vidéo. La vue d'arrivée est « recul » dans la config de la scène ([gauche, haut, zoom], en fractions de la pièce,
     pour les rendus élargis) ; sinon toute la pièce. Chaque image de l'animation est posée à sa taille (poser()) :
     jamais une mise à l'échelle par la carte graphique, qui rendrait le cadre flou. Rien ne bouge pour qui demande
     moins d'animation. */
  var RECUL = { duree: 7000, attente: 400, seuil: 0.6 };
  var moinsDAnimation = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function adoucir(t) { return 0.5 - 0.5 * Math.cos(Math.PI * t); }
  /* Les cadres clairs (fiche parcours-scenes-v4-moteur, 21/10) : un liseré clair bordait le blanc et le bouleau,
     le bord de la lumière de l'atlas (Buffet) ou le bord de la moulure rendue (Vinyles), clairs tous les deux. Le
     cadre du produit et la moulure se posent un peu en retrait de leurs coins (1,5 texel du calque le plus gros,
     un pixel au moins) : ce bord tombe sur le mur. En attendant la correction des images (fiche app-studio-liseret). */
  var LISERE = { blanc: true, bouleau: true };
  /* Les largeurs que la vue principale demande déjà au CDN : même fichier, déjà en cache. */
  var LARGEURS = [493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946];
  var LARGEURS_PIECE = [720, 1080, 1440, 1800];

  function avecLargeur(url, w) {
    if (!url) return url;
    if (/([?&])width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=' + w);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }
  function choisir(liste, besoin) {
    for (var i = 0; i < liste.length; i++) if (liste[i] >= besoin) return liste[i];
    return liste[liste.length - 1];
  }
  function dpr() { return Math.min(window.devicePixelRatio || 1, 2.5); }
  /* La photo du tirage, elle, suit l'écran jusqu'à 3× (mockups-2-montage, 25/10) : à 2,5, un A2 au téléphone ou en
     plein écran était agrandi de 1,1 à 1,3 fois, plus mou que le bois du mur autour. */
  function dprCadre() { return Math.min(window.devicePixelRatio || 1, 3); }
  function largeurDe(url) { var m = /[?&]width=(\d+)/.exec(url || ''); return m ? +m[1] : 0; }

  /* Une image prête à s'afficher (déjà décodée) : on bascule sans éclair. */
  var promesses = {};
  function precharger(url) {
    if (!url) return Promise.reject(new Error('url'));
    if (promesses[url]) return promesses[url];
    promesses[url] = new Promise(function (ok, ko) {
      var im = new Image();
      im.decoding = 'async';
      im.onload = function () {
        if (im.decode) im.decode().then(function () { ok(im); }, function () { ok(im); });
        else ok(im);
      };
      im.onerror = function () { delete promesses[url]; ko(new Error(url)); };
      im.src = url;
    });
    return promesses[url];
  }

  /* Les calques de la voie mixte, du fond vers l'avant, posés au-dessus de la photo du tirage. */
  var CALQUES = ['interieur', 'moulure', 'reflet'];
  /* fixe : le format d'une part de la scène des trois tailles (ses éléments portent data-bs-f) */
  function quel(n, fixe) { return '.bisou-scene__' + n + (fixe ? '[data-bs-f="' + fixe + '"]' : ''); }
  function calquesMixte(stage, fixe) {
    var o = {};
    CALQUES.forEach(function (n) {
      var im = stage.querySelector(quel(n, fixe));
      if (!im) {
        im = document.createElement('img');
        im.className = 'bisou-scene__' + n;
        if (fixe) im.setAttribute('data-bs-f', fixe);
        im.alt = '';
        im.decoding = 'async';
        im.setAttribute('aria-hidden', 'true');
        stage.appendChild(im);
      }
      o[n] = im;
    });
    return o;
  }

  function sprite(el, atlas, a) {
    /* a = [x, y, w, h] dans l'atlas : la case remplit exactement l'élément, à toute taille */
    var AW = atlas.w, AH = atlas.h, x = a[0], y = a[1], w = a[2], h = a[3];
    el.style.backgroundImage = 'url("' + atlas.url + '")';
    el.style.backgroundSize = (AW / w * 100) + '% ' + (AH / h * 100) + '%';
    el.style.backgroundPosition = (AW === w ? 0 : x / (AW - w) * 100) + '% ' + (AH === h ? 0 : y / (AH - h) * 100) + '%';
  }

  /* ── Une scène ── (fixe : une part de la scène des trois tailles, toujours à ce format) */
  function Scene(li, prod, s, fixe) {
    this.li = li;
    this.p = prod;
    this.s = s;
    this.fixe = fixe || null;
    this.stage = li.querySelector('.bisou-scene');
    this.piece = li.querySelector('.bisou-scene__piece');
    this.ombre = li.querySelector(quel('ombre', fixe));
    this.cadre = li.querySelector(quel('cadre', fixe));
    this.lumiere = li.querySelector(quel('lumiere', fixe));
    this.mixte = s.mixte && s.mixte.cas ? s.mixte : null;
    this.calques = this.mixte ? calquesMixte(this.stage, this.fixe) : null;
    if (this.mixte && this.mixte.reflet != null) this.stage.style.setProperty('--bs-reflet', this.mixte.reflet);
    /* la CSS cale la lumière par scène ; le plein écran copie la boîte, donc l'attribut aussi (mockups-2b) */
    this.stage.setAttribute('data-scene', s.id);
    this.sansCadre = false;
    this.fmt = null;
    this.coul = null;
    this.jeton = 0;
    this.actif = false;
    this.suitLaLargeur = true;   /* la diapo de la galerie ; le plein écran (clone) garde toute la pièce */
    this.base = cadragePour(s.id, prod.o, this.stage.clientWidth);
    this.baseRecul = s.recul && s.recul.length === 3 ? s.recul : TOUTE_LA_PIECE;
    this.t = 0;
    this.cadrer();
    var self = this;
    this.cadre.addEventListener('load', function () { self.poser(); });
    if ('ResizeObserver' in window) new ResizeObserver(function () { self.cadrer(); self.poser(); }).observe(this.stage, { box: 'border-box' });
    else window.addEventListener('resize', function () { self.cadrer(); self.poser(); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        /* au moins RECUL.seuil de la diapo à l'écran : isIntersecting ne suit le seuil que dans Chromium (pour le W3C,
           et peut-être Safari, il est vrai au simple contact) ; 1 % de marge pour l'arrondi du rapport */
        var e = es[es.length - 1];
        self.visible = e.isIntersecting && e.intersectionRatio >= RECUL.seuil - 0.01;
        self.reculer();
      }, { threshold: RECUL.seuil }).observe(li);
    }
  }

  /* La pièce agrandie et décalée pour ne montrer que la partie choisie (fixe : ne dépend pas du format), à la forme
     de la boîte, au point t du recul. Les calques de la voie mixte couvrent toute la pièce : ils prennent exactement
     sa boîte. */
  Scene.prototype.cadrer = function () {
    var W = this.stage.clientWidth, H = this.stage.clientHeight, r = parseFloat(this.s.ratio) || 1.25;
    if (this.suitLaLargeur && W) this.base = cadragePour(this.s.id, this.p.o, W);
    var a = vuePour(this.base, r, W && H ? W / H : r), v = a;
    if (this.t > 0 && this.baseRecul) {
      var b = vuePour(this.baseRecul, r, W && H ? W / H : r), t = adoucir(this.t);
      var lx = 1 / a[2] + (1 / b[2] - 1 / a[2]) * t, ly = 1 / a[3] + (1 / b[3] - 1 / a[3]) * t;
      v = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, 1 / lx, 1 / ly];
    }
    this.vue = v;
    var els = [this.piece], calques = this.calques;
    if (calques) CALQUES.forEach(function (n) { els.push(calques[n]); });
    els.forEach(function (el) {
      var st = el.style;
      st.left = (-v[0] * v[2] * 100) + '%';
      st.top = (-v[1] * v[3] * 100) + '%';
      st.setProperty('width', (v[2] * 100) + '%', 'important');
      st.setProperty('height', (v[3] * 100) + '%', 'important');
    });
  };

  /* Le recul, une fois : la diapo visible, la pièce et le cadre posés. */
  Scene.prototype.reculer = function () {
    var tel = CADRAGE_TELEPHONE[this.s.id] && this.base === CADRAGE_TELEPHONE[this.s.id][this.p.o];
    if (this.reculLance || tel || !this.visible || !this.li.classList.contains('is-ready') ||
        !this.li.classList.contains('is-posee') || (moinsDAnimation && moinsDAnimation.matches)) return;
    var a = this.base, b = this.baseRecul;
    if (!b || (a[0] === b[0] && a[1] === b[1] && a[2] === b[2])) return;
    this.reculLance = true;
    var self = this, debut = 0;
    var pas = function (now) {
      if (!debut) debut = now;
      self.t = Math.min(1, (now - debut) / RECUL.duree);
      self.cadrer();
      self.poser();
      if (self.t < 1) requestAnimationFrame(pas);
    };
    setTimeout(function () { requestAnimationFrame(pas); }, RECUL.attente);
  };

  /* Les trois calques de la voie mixte pour ce cas, ou null (le cas garde la lumière de l'atlas). */
  Scene.prototype.urlsMixte = function (fmt, coul) {
    var cas = this.mixte && this.mixte.cas[fmt];
    var paire = cas && cas[coul];
    if (!paire || !paire[0] || !paire[1] || !cas.reflet) return null;
    return { moulure: paire[0], interieur: paire[1], reflet: cas.reflet };
  };

  Scene.prototype.urlCadre = function (fmt, coul) {
    var c = this.p.cadres || {};
    var url = c[fmt + '-' + coul] || null;
    if (!url) return null;
    var cont = CONTOUR[this.p.o][fmt];
    var cas = this.s.formats[fmt];
    var largeurCadre = (cas.c[2] - cas.c[0]) * (this.stage.clientWidth || 600) * this.vue[2];
    var besoin = largeurCadre * dprCadre() / Math.max(0.3, cont[2] - cont[0]);
    return avecLargeur(url, choisir(LARGEURS, besoin));
  };

  /* La photo plus fine si le cadre s'affiche plus grand que prévu (diapo élargie, plein écran) : bw = la largeur où
     l'image est posée, en pixels CSS. Jamais plus petite : une image déjà là suffit en reculant. On ne remplace que
     la même image, plus petite : un format ou une finition changé entre-temps garde sa propre image. */
  Scene.prototype.affiner = function (bw) {
    var src = this.cadre.getAttribute('src'), w = largeurDe(src);
    if (!w || w >= LARGEURS[LARGEURS.length - 1] || bw * dprCadre() <= w) return;
    var url = avecLargeur(src, choisir(LARGEURS, bw * dprCadre()));
    if (url === src || url === this.affinage) return;
    this.affinage = url;
    var self = this;
    precharger(url).then(function () {
      if (self.affinage === url) self.affinage = null;
      var cur = self.cadre.getAttribute('src');
      if (avecLargeur(cur, 1) === avecLargeur(url, 1) && largeurDe(cur) < largeurDe(url)) self.cadre.setAttribute('src', url);
    }, function () {});   /* en échec : pas de nouvel essai à chaque image du recul */
  };

  Scene.prototype.couleurPour = function (fmt, voulue) {
    var c = this.p.cadres || {};
    var essais = [voulue, this.coul, this.p.couleur, 'noir', 'blanc', 'chene', 'bouleau'];
    for (var i = 0; i < essais.length; i++) if (essais[i] && c[fmt + '-' + essais[i]]) return essais[i];
    return null;
  };

  /* La scène changerait-elle si l'état devenait (fmt, voulue) ? (la galerie s'en sert pour rester dessus) */
  Scene.prototype.changerait = function (fmt, voulue) {
    if (this.fixe) fmt = this.fixe;
    if (!this.s.formats[fmt]) return false;
    return fmt !== this.fmt || this.couleurPour(fmt, voulue) !== this.coul;
  };

  /* Une scène sans ce format (la tablette n'a que l'A4 et l'A3) cache sa diapo, et la rend quand le format revient ;
     masquer() est définitif, cet attribut-ci non. Rend vrai si la diapo a changé. */
  Scene.prototype.horsFormat = function (fmt) {
    var hors = !this.fixe && !this.s.formats[fmt], li = this.li;
    if (hors === li.hasAttribute('data-bisou-hors-format')) return false;
    if (hors) {
      li.setAttribute('data-bisou-hors-format', '');
      li.hidden = true;
      li.style.setProperty('display', 'none', 'important');
    } else {
      li.removeAttribute('data-bisou-hors-format');
      if (li.hasAttribute('data-bisou-masquee')) return false;
      li.hidden = false;
      li.style.removeProperty('display');
    }
    return true;
  };

  /* « Sans cadre » choisi : les scènes gardent un cadre (Max, 19/10) et une ligne discrète dit lequel (mockups-2b). */
  var MENTION = {
    fr: { noir: 'Montré avec le cadre noir', blanc: 'Montré avec le cadre blanc', chene: 'Montré avec le cadre chêne', bouleau: 'Montré avec le cadre bouleau' },
    en: { noir: 'Shown in a black frame', blanc: 'Shown in a white frame', chene: 'Shown in an oak frame', bouleau: 'Shown in a birch frame' }
  };
  Scene.prototype.mentionner = function (coul) {
    var txt = this.sansCadre && MENTION[(document.documentElement.lang || '').indexOf('en') === 0 ? 'en' : 'fr'][coul];
    var m = this.stage.querySelector('.bisou-scene__mention');
    if (!txt) { if (m) m.hidden = true; return; }
    if (!m) {
      m = document.createElement('p');
      m.className = 'bisou-scene__mention';
      this.stage.appendChild(m);
    }
    m.textContent = txt;
    m.hidden = false;
    this.placerMention();
  };
  /* La boîte de la scène peut déborder de la diapo, qui la rogne (à l'ordinateur : 19 px à gauche à 1 440, 91 px en
     haut à 1 905) : la mention se pose dans la partie visible. */
  Scene.prototype.placerMention = function () {
    var m = this.stage.querySelector('.bisou-scene__mention');
    if (!m || m.hidden) return;
    var sr = this.stage.getBoundingClientRect(), lr = this.li.getBoundingClientRect();
    m.style.marginLeft = Math.max(0, Math.round(lr.left - sr.left)) + 'px';
    m.style.marginTop = Math.max(0, Math.round(lr.top - sr.top)) + 'px';
  };

  Scene.prototype.aller = function (fmt, voulue) {
    var self = this;
    if (this.fixe) fmt = this.fixe;
    if (!this.s.formats[fmt]) return;
    var coul = this.couleurPour(fmt, voulue);
    if (!coul) { this.li.classList.add('is-empty'); return; }
    this.sansCadre = !voulue;
    this.mentionner(coul);
    if (fmt === this.fmt && coul === this.coul) { this.jeton++; return; }   /* un chargement en cours pour un autre état ne s'applique plus */
    var jeton = ++this.jeton;
    var appliquer = function (url, mx) {
      if (jeton !== self.jeton) return;
      self.fmt = fmt;
      self.coul = coul;
      if (url) self.cadre.setAttribute('src', url);
      if (self.calques) {
        if (mx) CALQUES.forEach(function (n) { self.calques[n].setAttribute('src', mx[n]); });
        self.stage.classList.toggle('is-mixte', !!mx);
      }
      self.li.setAttribute('data-format', fmt);
      self.li.setAttribute('data-couleur', coul);
      self.poser();
    };
    if (!this.actif) {
      /* pas encore affichée : on note seulement, rien ne se charge */
      this.fmt = fmt;
      this.coul = coul;
      return;
    }
    var url = this.urlCadre(fmt, coul);
    var mx = this.urlsMixte(fmt, coul);
    /* la photo et les calques du cas s'affichent ensemble : jamais un trou vide, ni la moulure d'un autre format */
    var attentes = [precharger(url).then(null, function () {})];
    if (mx) CALQUES.forEach(function (n) { attentes.push(precharger(mx[n]).then(null, function () { mx = null; })); });
    Promise.all(attentes).then(function () { appliquer(url, mx); });
  };

  Scene.prototype.activer = function () {
    if (this.actif || this.li.hasAttribute('data-bisou-masquee') || this.li.hasAttribute('data-bisou-hors-format')) return;
    this.actif = true;
    var self = this;
    var w = this.stage.clientWidth || 600;
    var urlPiece = avecLargeur(this.s.piece, choisir(LARGEURS_PIECE, w * dpr() * this.vue[2]));
    var fmt = this.fmt || 'A2', coul = this.coul, sansCadre = this.sansCadre;
    this.fmt = null;
    this.coul = null;
    precharger(urlPiece).then(function () {
      self.piece.setAttribute('src', urlPiece);
      self.li.classList.add('is-ready');
      self.reculer();
    }, function () {
      self.li.classList.add('is-broken');
      document.dispatchEvent(new CustomEvent('bisou:scene-broken', { detail: { id: self.s.id } }));
    });
    this.aller(fmt, coul);
    /* la couleur notée est passée comme voulue : « Sans cadre » reste ce qu'il était */
    this.sansCadre = sansCadre;
    this.mentionner(coul);
  };

  Scene.prototype.poser = function () {
    var im = this.cadre, st = this.stage;
    if (!this.fmt || !st) return;
    var cas = this.s.formats[this.fmt];
    if (!cas) return;
    var W = st.clientWidth, H = st.clientHeight;
    if (!W || !H) return;
    this.placerMention();
    var c = cas.c;
    /* fractions de la pièce -> fractions de la diapo, selon la partie montrée (zoom en largeur z, en hauteur zy) */
    var v = this.vue, z = v[2], zy = v[3];
    var X = function (f) { return (f - v[0]) * z; }, Y = function (f) { return (f - v[1]) * zy; };
    var x0 = Math.min(c[0], c[6]), x1 = Math.max(c[2], c[4]), y0 = Math.min(c[1], c[3]), y1 = Math.max(c[5], c[7]);
    /* le retrait d'un cadre clair, en pixels de la diapo (0 pour les autres) */
    var d = 0;
    if (LISERE[this.coul]) {
      var texel = W * z / ((this.s.res && this.s.res[0]) || 1800);
      if (cas.lumiere && cas.lumiere.a && cas.lumiere.a[2]) texel = Math.max(texel, (x1 - x0) * W * z / cas.lumiere.a[2]);
      d = Math.max(1, 1.5 * texel);
    }
    /* Voie mixte, cadre clair (reprise du 21/10) : l'ombre s'arrête au contour du cadre ; le retrait découvrait une
       bande de mur sans ombre, plus claire que le mur ombré et que le bouleau. L'ombre est resserrée autour du centre
       du cadre, juste assez pour que son bord suive le cadre rentré. */
    var mixte = d && this.calques && st.classList.contains('is-mixte');
    /* l'ombre sur le mur */
    if (this.ombre && cas.ombre && this.s.atlas) {
      var b = cas.ombre.b, cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
      var ex = mixte ? 1 - 2 * d / ((x1 - x0) * W * z) : 1, ey = mixte ? 1 - 2 * d / ((y1 - y0) * H * zy) : 1;
      this.ombre.style.left = (X(cx + (b[0] - cx) * ex) * 100) + '%';
      this.ombre.style.top = (Y(cy + (b[1] - cy) * ey) * 100) + '%';
      this.ombre.style.width = ((b[2] - b[0]) * ex * z * 100) + '%';
      this.ombre.style.height = ((b[3] - b[1]) * ey * zy * 100) + '%';
      sprite(this.ombre, this.s.atlas, cas.ombre.a);
    }
    /* la lumière sur le cadre : exactement la boîte du cadre */
    if (this.lumiere && cas.lumiere && this.s.atlas) {
      this.lumiere.style.left = (X(x0) * 100) + '%';
      this.lumiere.style.top = (Y(y0) * 100) + '%';
      this.lumiere.style.width = ((x1 - x0) * z * 100) + '%';
      this.lumiere.style.height = ((y1 - y0) * zy * 100) + '%';
      sprite(this.lumiere, this.s.atlas, cas.lumiere.a);
    }
    /* le cadre du produit, sur ses quatre coins, rogné à son contour.
       L'image est posée à peu près à sa taille d'arrivée (la matrice ne fait plus qu'ajuster) : c'est le
       navigateur qui la réduit, avec son bon filtre, et non la carte graphique (sinon : crénelage sur la photo). */
    if (this.calques) {
      var m = this.calques.moulure;
      if (!d) m.style.clipPath = '';
      else {
        /* la moulure couvre toute la pièce : ses coins en fractions de la pièce, rentrés de d */
        var dx = d / (W * z), dy = d / (H * zy);
        var pt = function (x, y) { return (x * 100).toFixed(4) + '% ' + (y * 100).toFixed(4) + '%'; };
        m.style.clipPath = 'polygon(' + pt(c[0] + dx, c[1] + dy) + ',' + pt(c[2] - dx, c[3] + dy) + ',' +
          pt(c[4] - dx, c[5] - dy) + ',' + pt(c[6] + dx, c[7] - dy) + ')';
      }
    }
    var SW = im.naturalWidth, SH = im.naturalHeight;
    if (!SW || !SH || !im.getAttribute('src')) return;
    var k = CONTOUR[this.p.o][this.fmt];
    var largeurCadre = Math.max(Math.abs(c[2] - c[0]), Math.abs(c[4] - c[6])) * W * z;
    var BW = Math.max(60, Math.round(largeurCadre / Math.max(0.2, k[2] - k[0]))), BH = BW * SH / SW;
    /* rogné un peu à l'intérieur du contour, sur des pixels entiers : jamais de liseré du fond de l'image au bord
       du cadre. Le navigateur arrondit au pixel, vers l'extérieur, le rognage d'un calque en 3D, et le pixel du
       bord mêle le fond au cadre en réduisant l'image : un bord fractionnaire laissait passer une rangée du fond
       (liseré clair en haut ou à gauche d'un grand cadre, 20/10). Au moins un demi-pixel d'écran et 0,6 pixel de
       l'image source vers l'intérieur. */
    var r = Math.max(0.5, 0.6 * BW / SW);
    var sx0 = Math.ceil(k[0] * BW + r), sy0 = Math.ceil(k[1] * BH + r), sx1 = Math.floor(k[2] * BW - r), sy1 = Math.floor(k[3] * BH - r);
    im.style.width = BW + 'px';
    im.style.height = BH + 'px';
    im.style.clipPath = 'inset(' + sy0 + 'px ' + (BW - sx1) + 'px ' + (BH - sy1) + 'px ' + sx0 + 'px)';
    /* voie mixte : le cadre du produit, sous la moulure, rentré d'au moins un pixel d'écran de plus qu'elle. Sur le
       même bord, les deux arêtes adoucies laissaient passer son bois clair du studio (un pixel clair, bord gauche). */
    var dc = mixte ? d + Math.max(0.5, 1 / dpr()) : d;
    im.style.transform = matrix3d([[sx0, sy0], [sx1, sy0], [sx1, sy1], [sx0, sy1]],
                                  [[X(c[0]) * W + dc, Y(c[1]) * H + dc], [X(c[2]) * W - dc, Y(c[3]) * H + dc],
                                   [X(c[4]) * W - dc, Y(c[5]) * H - dc], [X(c[6]) * W + dc, Y(c[7]) * H - dc]]);
    this.li.classList.add('is-posee');
    this.affiner(BW);
    this.reculer();
  };

  /* ── La scène des trois tailles (« tous » dans la config ; Max, 26/10 : « une avec les trois tailles de tirages pour
     comparer ») : l'A4, l'A3 et l'A2 du tirage à la fois, chacun à sa place dans la pièce, dans la finition choisie.
     Trois parts (une Scene par format, ses éléments marqués data-bs-f) sur la même pièce ; le choix du format ne
     change pas cette diapo. La vue en grand et son détail se centrent sur l'A3, au milieu. ── */
  function Trois(li, prod, s) {
    this.li = li;
    this.p = prod;
    this.s = s;
    this.stage = li.querySelector('.bisou-scene');
    this.parts = ['A4', 'A3', 'A2'].filter(function (f) { return s.formats[f]; })
      .map(function (f) { return new Scene(li, prod, s, f); });
    this.milieu = this.parts[Math.floor(this.parts.length / 2)] || null;
  }
  ['aller', 'activer', 'cadrer', 'poser'].forEach(function (n) {
    Trois.prototype[n] = function () {
      var a = arguments;
      this.parts.forEach(function (p) { p[n].apply(p, a); });
    };
  });
  Trois.prototype.changerait = function (fmt, voulue) {
    return this.parts.some(function (p) { return p.changerait(fmt, voulue); });
  };
  Object.defineProperties(Trois.prototype, {
    actif: { get: function () { return this.parts.length > 0 && this.parts.every(function (p) { return p.actif; }); } },
    fmt: { get: function () { return this.milieu ? this.milieu.fmt : null; } },
    coul: { get: function () { return this.milieu ? this.milieu.coul : null; } },
    mixte: { get: function () { return this.milieu ? this.milieu.mixte : null; } }
  });

  /* ── Plein écran d'une scène ── */
  var vue = null;
  /* La vue en grand a sa propre entrée d'historique (fiche b27-fiche-expand-deux-vues, 27/10) : le bouton retour du
     navigateur (et le geste retour du téléphone) la ferme au lieu de quitter la fiche ; la croix, Échap et le fond
     la ferment par le même chemin, pour ne pas laisser derrière eux une entrée qui ne fait rien. */
  var VUE_HISTOIRE = 'bisouSceneVue';
  function dansHistoire() { return !!(history.state && history.state[VUE_HISTOIRE]); }
  function etatSans(avec) {
    var st = {}, h = history.state;
    if (h && typeof h === 'object') for (var k in h) if (k !== VUE_HISTOIRE) st[k] = h[k];
    if (avec) st[VUE_HISTOIRE] = 1;
    return st;
  }
  function quitterVue() {
    if (!vue) return;
    if (vue.histoire && dansHistoire()) {
      /* une seule fois : un double clic sur la croix ou le fond reculait deux fois et quittait la fiche (relecture, 27/10) */
      if (!vue.recule) { vue.recule = true; history.back(); }
    }
    else fermerVue();
  }
  window.addEventListener('popstate', function () { if (vue && !dansHistoire()) fermerVue(); });
  /* la scène voisine dans l'ordre de la galerie (pas : 1 ou -1) ; posée par init() */
  var voisine = function () { return null; };
  function fermerVue(suite, garder) {
    if (!vue) return;
    var v = vue;
    vue = null;
    document.removeEventListener('keydown', v.clavier, true);
    /* vers la scène voisine : l'ancienne vue part d'un coup sous la nouvelle, sans fondu ni focus rendu ; au doigt
       (garder), elle reste dessous et glisse avec la nouvelle, qui l'enlève à la fin */
    if (suite) { if (!garder && v.el.parentNode) v.el.parentNode.removeChild(v.el); return; }
    v.el.classList.remove('is-open');
    document.documentElement.classList.remove('bisou-scene-vue-ouverte');
    setTimeout(function () { if (v.el.parentNode) v.el.parentNode.removeChild(v.el); }, 260);
    if (v.retour && v.retour.focus) { try { v.retour.focus({ preventScroll: true }); } catch (e) {} }
  }
  /* Une copie de la scène dans une boîte de la vue en grand, sur la partie base ([gauche, haut, zoom]) de la pièce. */
  function copierScene(scene, el, boite, base, largeurPiece) {
    if (scene.parts) {
      /* les trois tailles : une copie par part, dans la même boîte */
      var copies = scene.parts.map(function (p) { return copierScene(p, el, boite, base, largeurPiece); });
      return {
        piece: copies[0].piece,
        prete: Promise.all(copies.map(function (c) { return c.prete; })),
        cadrer: function () { copies.forEach(function (c) { c.cadrer(); }); },
        poser: function () { copies.forEach(function (c) { c.poser(); }); }
      };
    }
    var clone = Object.create(Scene.prototype);
    clone.li = el; clone.p = scene.p; clone.s = scene.s; clone.stage = boite;
    clone.fixe = scene.fixe || null;
    clone.piece = boite.querySelector('.bisou-scene__piece');
    clone.ombre = boite.querySelector(quel('ombre', clone.fixe));
    clone.cadre = boite.querySelector(quel('cadre', clone.fixe));
    clone.lumiere = boite.querySelector(quel('lumiere', clone.fixe));
    clone.mixte = scene.mixte;
    clone.calques = scene.calques ? calquesMixte(boite, clone.fixe) : null;
    clone.fmt = scene.fmt; clone.coul = scene.coul; clone.jeton = 0; clone.actif = true;
    clone.base = base; clone.t = 0;
    clone.cadrer();
    /* la pièce déjà affichée d'abord (copiée avec la boîte, en cache), la grande une fois prête : pas d'éclair */
    var pieceGrande = avecLargeur(scene.s.piece, largeurPiece);
    var attentePiece = precharger(pieceGrande);
    if (!clone.piece.getAttribute('src')) clone.piece.setAttribute('src', pieceGrande);
    else attentePiece.then(function () { clone.piece.setAttribute('src', pieceGrande); }, function () {});
    clone.cadre.addEventListener('load', function () { clone.poser(); });
    var grand = clone.urlCadre(scene.fmt, scene.coul);
    var actuel = scene.cadre.getAttribute('src');
    var attenteCadre = precharger(grand);
    attenteCadre.then(function () { clone.cadre.setAttribute('src', grand); }, function () {});
    if (actuel) clone.cadre.setAttribute('src', actuel);
    /* la vue nette : ses voisines se chargent ensuite (fiche c29u-vue-en-grand-fluide) */
    clone.prete = Promise.all([attentePiece, attenteCadre].map(function (p) { return p.then(null, function () {}); }));
    if ('ResizeObserver' in window) new ResizeObserver(function () { clone.cadrer(); clone.poser(); }).observe(boite, { box: 'border-box' });
    return clone;
  }
  /* Une vue à la fois, en grand et nette (Max, 28/10 : « quand on fait expand, ça vient juste zoomer dans l'image. Mais
     c'est pas ça que j'ai demandé » ; « je vois pas les flèches quand je fais expand » ; fiche c28-fiche-galerie-expand).
     Le « détail » posé le 27/10 (la même pièce recadrée autour du cadre) est retiré : c'était ce zoom, et agrandi trois
     fois, il était flou. On passe d'une scène à l'autre par des flèches, le clavier et, au téléphone, un glisser (une
     image par geste) ; un compteur discret dit où l'on en est. La pièce est chargée à la taille de l'écran
     (densité comprise), 2 400 px au moins. Les rendus « ambiance » des scènes portent une autre photo au mur : ils ne se
     montrent pas à côté d'un tirage. */
  var rang = function () { return { i: -1, n: 0 }; };
  var prechauffer = function () {};
  /* les vues voisines, la suivante d'abord, sans bouger la galerie ; posée par init() */
  var voisines = function () { return []; };
  /* les bords gauche et droit des cadres d'une scène (les trois tailles : des trois), en fractions de la pièce */
  function bordsCadres(scene) {
    var g = 1, dr = 0;
    (scene.parts ? scene.parts.map(function (p) { return p.fixe; }) : [scene.fmt]).forEach(function (f) {
      var cf = scene.s.formats[f] && scene.s.formats[f].c;
      if (cf) { g = Math.min(g, cf[0], cf[6]); dr = Math.max(dr, cf[2], cf[4]); }
    });
    return [g, dr];
  }
  /* la largeur de la boîte de la vue, en points (la CSS de .bisou-scene-vue__vues) */
  function largeurBoiteVue(ratio) {
    var r = parseFloat(ratio) || 1.25, W = window.innerWidth, H = window.innerHeight || 1;
    if (TEL.matches) return H * r;
    var rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
    return Math.max(1, Math.min(H - 8 * rem, (W - 16 * rem) / r) * r);
  }
  /* Au téléphone, la pièce à la taille où elle s'affiche (fiche c29u-vue-en-grand-fluide ; Max, 29/10 : « ça charge pas
     mal, c'est assez lent ») : toute la hauteur de l'écran, rapetissée comme la vue le fera si ses cadres n'y tiennent
     pas, à deux pixels par point, 2 400 au plus ; le plancher de 2 400 px ne vaut plus qu'à l'ordinateur. */
  function largeurPieceVue(ratio, scene) {
    if (TEL.matches) {
      var w = largeurBoiteVue(ratio), bc = scene && scene.s ? bordsCadres(scene) : [1, 0];
      var besoin = (bc[1] - bc[0]) * w + 2 * ECART;
      if (bc[1] > bc[0] && besoin > window.innerWidth) w *= window.innerWidth / besoin;
      return Math.max(800, Math.min(2400, Math.ceil(w * Math.min(window.devicePixelRatio || 1, 2) / 200) * 200));
    }
    var css = Math.min(window.innerWidth, (window.innerHeight || 1) * (parseFloat(ratio) || 1.25));
    return Math.max(2400, Math.min(3600, Math.ceil(css * Math.min(window.devicePixelRatio || 1, 3) / 400) * 400));
  }
  /* le tirage seul : au téléphone, il prend la largeur de l'écran (densité comprise), pas 2 400 px */
  function largeurTirageVue(ratio) {
    if (!TEL.matches) return largeurPieceVue(ratio);
    return Math.min(2400, Math.ceil(window.innerWidth * Math.min(window.devicePixelRatio || 1, 3) / 200) * 200);
  }
  function urlTirageVue(img, w) {
    var src = img.currentSrc || img.src;
    return largeurDe(src) >= w ? src : avecLargeur(src, w);
  }
  /* Ce que la vue d'une voisine chargera, chargé d'avance (fiche c29u-vue-en-grand-fluide) : un glisser trouve ses
     images prêtes. Les mêmes adresses que copierScene() et nette(), à la même largeur. */
  function prechargerVue(sc) {
    var urls = [];
    if (sc.tirage) {
      var im = sc.li.querySelector('img');
      if (!im || !(im.currentSrc || im.src)) return Promise.resolve();
      var ri = im.naturalWidth && im.naturalHeight ? +(im.naturalWidth / im.naturalHeight).toFixed(4) : 1.25;
      urls.push(im.currentSrc || im.src, urlTirageVue(im, largeurTirageVue(ri)));
    } else {
      if (!sc.fmt || !sc.s) return Promise.resolve();
      var W = largeurBoiteVue(sc.s.ratio);
      urls.push(avecLargeur(sc.s.piece, largeurPieceVue(sc.s.ratio, sc)));
      (sc.parts || [sc]).forEach(function (p) {
        [p.piece, p.cadre, p.ombre, p.lumiere].forEach(function (n) { if (n && n.getAttribute) urls.push(n.getAttribute('src')); });
        if (p.calques) CALQUES.forEach(function (n) { if (p.calques[n]) urls.push(p.calques[n].getAttribute('src')); });
        try { urls.push(Scene.prototype.urlCadre.call({ p: p.p, s: p.s, stage: { clientWidth: W }, vue: [0, 0, 1, 1] }, p.fmt, p.coul)); } catch (e) {}
      });
    }
    return Promise.all(urls.filter(Boolean).map(function (u) { return precharger(u).then(null, function () {}); }));
  }
  /* la vue qui part au doigt, le temps que la voisine arrive (posée par partir(), lue par ouvrirVue()) */
  var sortie = null;
  var DUREE_GLISSE = 240;
  var CHEVRON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  /* au téléphone, l'écart entre deux vues qui se suivent au doigt, et l'air laissé autour des cadres */
  var TEL = window.matchMedia('(max-width: 749px)');
  var CALME = window.matchMedia('(prefers-reduced-motion: reduce)');
  var ECART = 24;
  /* arrivee : 1 ou -1, la vue arrive du bord droit ou gauche (glisser au téléphone) */
  function ouvrirVue(scene, retour, arrivee) {
    /* le tirage seul (une diapo de la galerie qui n'est pas une scène) est la première vue, avant les scènes */
    var tirage = !!(scene && scene.tirage);
    var source = tirage ? scene.li.querySelector('img') : null;
    /* les vues de profil se préparent à la première ouverture de la vue, pas avant */
    prechauffer();
    if (scene && scene.parent && !scene.fmt) {
      /* une vue de profil encore en chargement (son format se pose à la fin) : elle s'ouvre dès qu'elle est prête, si la
         vue n'a ni fermé ni changé de scène entre-temps (Échap ou ← pendant l'attente : elle ne revient pas d'elle-même) */
      var attendue = scene, depuis = vue, essais = 0;
      var encore = function () {
        if (vue !== depuis || attendue.li.classList.contains('is-broken')) return;
        if (attendue.fmt) ouvrirVue(attendue, retour); else if (++essais < 40) setTimeout(encore, 150);
      };
      setTimeout(encore, 150);
      return;
    }
    if (!scene || (tirage ? !source : (!scene.fmt || !scene.actif || scene.li.hasAttribute('data-bisou-hors-format')))) return;
    /* d'une scène à la voisine (flèches) : la même entrée d'historique, le même bouton où revenir */
    var suite = !!vue;
    var histoire = !!(vue && vue.histoire);
    if (vue) retour = retour || vue.retour;
    var depart = arrivee && suite && sortie && sortie.el === vue.el && TEL.matches && !CALME.matches ? sortie : null;
    fermerVue(suite, !!depart);
    var el = document.createElement('div');
    el.className = 'bisou-scene-vue';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    var boite, ratio, image = null;
    if (tirage) {
      ratio = source.naturalWidth && source.naturalHeight ? +(source.naturalWidth / source.naturalHeight).toFixed(4) : 1.25;
      el.setAttribute('aria-label', source.alt || '');
      boite = document.createElement('div');
      boite.className = 'bisou-scene--vue bisou-scene-vue__tirage';
      image = document.createElement('img');
      image.alt = source.alt || '';
      image.src = source.currentSrc || source.src;
      boite.appendChild(image);
    } else {
      ratio = scene.s.ratio;
      el.setAttribute('aria-label', scene.stage.getAttribute('aria-label') || '');
      boite = scene.li.querySelector('.bisou-scene').cloneNode(true);
      boite.classList.add('bisou-scene--vue');
    }
    boite.style.setProperty('--bs-ratio', ratio);
    var vues = document.createElement('div');
    vues.className = 'bisou-scene-vue__vues';
    vues.style.setProperty('--bs-ratio', ratio);
    vues.appendChild(boite);
    var en = (document.documentElement.lang || '').indexOf('en') === 0;
    var fermer = document.createElement('button');
    fermer.type = 'button';
    fermer.className = 'bisou-scene-vue__fermer';
    fermer.setAttribute('aria-label', en ? 'Close' : 'Fermer');
    fermer.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/></svg>';
    el.appendChild(vues);
    /* les flèches des côtés et le compteur : les libellés des flèches de la galerie, déjà traduits */
    var r = rang(scene);
    var aller = function (pas, glisse) {
      var v2 = voisine(scene, pas);
      if (v2) ouvrirVue(v2, null, glisse ? pas : 0);
      return !!v2;
    };
    /* la vue suit le doigt (x en px), ou se cale en duree ms */
    var decaler = function (x, duree) {
      if (CALME.matches) duree = 0;
      vues.style.transition = duree ? 'transform ' + duree + 'ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none';
      vues.style.transform = x ? 'translate3d(' + Math.round(x) + 'px, 0, 0)' : '';
    };
    /* au téléphone : la vue part d'un côté, la voisine arrive de l'autre ; au bout de la liste, elle revient */
    /* x : là où le doigt a laissé la vue. La voisine se construit tout de suite, à côté, et les deux glissent ensemble
       jusqu'à leur place en 240 ms (fiche c29u-vue-en-grand-fluide : avant, la vue sortait, un trou, puis la voisine
       entrait, près d'une demi-seconde) */
    var partir = function (pas, x) {
      if (!vue || vue.part) return;
      if (r.i < 0 || r.i + pas < 0 || r.i + pas >= r.n) { decaler(0, 260); return; }
      vue.part = true;
      var moi = vue;
      sortie = { el: el, vues: vues, x: x || 0 };
      aller(pas, true);
      sortie = null;
      /* une vue de profil encore en chargement s'ouvre plus tard : celle-ci revient en attendant */
      if (vue === moi) { moi.part = false; decaler(0, 260); }
    };
    var fleche = function (pas) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bisou-scene-vue__fleche bisou-scene-vue__fleche--' + (pas < 0 ? 'prec' : 'suiv');
      var modele = document.querySelector('[data-bisou-gallery="slider"] .slider-button--' + (pas < 0 ? 'prev' : 'next'));
      b.setAttribute('aria-label', (modele && modele.getAttribute('aria-label')) || (pas < 0 ? (en ? 'Previous' : 'Précédente') : (en ? 'Next' : 'Suivante')));
      b.innerHTML = CHEVRON;
      if (r.i < 0 || r.i + pas < 0 || r.i + pas >= r.n) b.disabled = true;
      b.addEventListener('click', function (e) { e.stopPropagation(); if (TEL.matches) partir(pas); else aller(pas); });
      return b;
    };
    if (r.n > 1) {
      el.appendChild(fleche(-1));
      el.appendChild(fleche(1));
      var compteur = document.createElement('p');
      compteur.className = 'bisou-scene-vue__compteur';
      compteur.textContent = (r.i + 1) + ' / ' + r.n;
      el.appendChild(compteur);
    }
    el.appendChild(fermer);
    document.body.appendChild(el);
    /* la grande image une fois prête, sur celle déjà affichée (en cache) : pas d'éclair */
    var nette = function (w) {
      var u = urlTirageVue(source, w);
      var p = precharger(u);
      p.then(function () { if (image.getAttribute('src') !== u) image.setAttribute('src', u); }, function () {});
      return p.then(null, function () {});
    };
    var clone = tirage ? null : copierScene(scene, el, boite, TOUTE_LA_PIECE, largeurPieceVue(ratio, scene));
    var prete = tirage ? nette(largeurTirageVue(ratio)) : clone.prete;
    /* ← et → passent à la scène voisine, dans l'ordre de la galerie, qui la suit derrière : fermée, la vue rend la
       galerie sur la dernière scène vue. Zoomée, la vue garde ses flèches pour défiler. */
    var clavier = function (e) {
      if (e.key === 'Escape') { e.preventDefault(); quitterVue(); }
      else if (e.key === 'Tab') {
        /* le focus tourne entre la croix et les flèches actives */
        e.preventDefault();
        var f = [].slice.call(el.querySelectorAll('button:not([disabled])'));
        var k = f.indexOf(document.activeElement);
        var s = f[(k + (e.shiftKey ? f.length - 1 : 1)) % f.length] || fermer;
        s.focus();
      }
      else if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && !el.classList.contains('is-zoom') && !e.altKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        aller(e.key === 'ArrowRight' ? 1 : -1);
      }
    };
    /* Au téléphone, un glisser = une image (Max, 29/10 : « quand on veut swiper, ça décale l'image. Nous on veut que ça
       change d'image » ; fiche c29m-vue-en-grand-tel). La pièce ne se parcourt plus du doigt : elle reste centrée sur
       son cadre ; la vue suit le doigt, puis se cale sur la voisine (un quart d'écran, ou un geste vif) ou revient. Au
       bout de la liste, elle résiste. Pincée (zoom du navigateur), le doigt parcourt l'image et ne change pas de vue. */
    var doigt = null;
    var zoomee = function () { return !!(window.visualViewport && window.visualViewport.scale > 1.01); };
    var lacher = function (e) {
      var d = doigt;
      doigt = null;
      if (!d || d.sens !== 'x') return;
      apresGlisse = true;
      setTimeout(function () { apresGlisse = false; }, 0);
      var t = d.pt - d.ppt, vite = t > 0 && e.timeStamp - d.pt < 100 ? (d.dx - d.px) / t : 0;
      var pas = d.dx < 0 ? 1 : -1;
      if (e.type === 'touchend' && (Math.abs(d.dx) > el.clientWidth * 0.25 || (Math.abs(d.dx) > 24 && vite * -pas > 0.3))) partir(pas, d.dx);
      else decaler(0, 260);
    };
    el.addEventListener('touchstart', function (e) {
      /* un second doigt pendant un glisser : la vue revient en place */
      if (doigt && doigt.dx) decaler(0, 260);
      doigt = null;
      /* la vue qui part encore sous la voisine (240 ms) ne prend plus le doigt : un retour vif posé sur elle sautait une image */
      if (!TEL.matches || e.touches.length !== 1 || zoomee() || !vue || vue.part || vue.el !== el) return;
      doigt = { x: e.touches[0].clientX, y: e.touches[0].clientY, sens: '', dx: 0, px: 0, pt: e.timeStamp, ppt: e.timeStamp };
    }, { passive: true });
    el.addEventListener('touchmove', function (e) {
      var d = doigt;
      if (!d) return;
      if (e.touches.length !== 1 || zoomee()) { doigt = null; decaler(0, 260); return; }
      var dx = e.touches[0].clientX - d.x, dy = e.touches[0].clientY - d.y;
      if (!d.sens) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        d.sens = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (d.sens !== 'x') return;
      if (e.cancelable) e.preventDefault();
      var bout = r.i < 0 || (dx < 0 ? r.i + 1 >= r.n : r.i <= 0);
      d.px = d.dx; d.ppt = d.pt; d.pt = e.timeStamp;
      d.dx = bout ? dx / 3 : dx;
      decaler(d.dx, 0);
    }, { passive: false });
    el.addEventListener('touchend', lacher, { passive: true });
    el.addEventListener('touchcancel', lacher, { passive: true });
    /* à partir de 750 px (tablette, écran tactile), le glisser d'avant, inchangé : un geste franc passe à la voisine */
    var tablette = null;
    el.addEventListener('touchstart', function (e) {
      tablette = TEL.matches || e.touches.length !== 1 ? null : { x: e.touches[0].clientX, y: e.touches[0].clientY, l: el.scrollLeft, max: el.scrollWidth - el.clientWidth };
    }, { passive: true });
    el.addEventListener('touchend', function (e) {
      var d = tablette;
      tablette = null;
      if (!d || !e.changedTouches.length || el.classList.contains('is-zoom')) return;
      var dx = e.changedTouches[0].clientX - d.x, dy = e.changedTouches[0].clientY - d.y;
      if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      if (dx < 0 && d.l >= d.max - 2) aller(1);
      else if (dx > 0 && d.l <= 2) aller(-1);
    }, { passive: true });
    vue = { el: el, retour: retour, clavier: clavier, histoire: histoire, scene: scene };
    var cetteVue = vue;
    Promise.resolve(prete).then(function () {
      if (vue !== cetteVue) return;
      voisines(scene).reduce(function (p, v) { return p.then(function () { return prechargerVue(v); }); }, Promise.resolve());
    });
    if (!histoire) {
      try { history.pushState(etatSans(true), ''); vue.histoire = true; } catch (e) {}
    }
    document.addEventListener('keydown', clavier, true);
    var apresGlisse = false;
    el.addEventListener('click', function (e) {
      if (apresGlisse || (vue && vue.el !== el)) return;
      if (e.target === el || e.target === vues || (e.target.closest && e.target.closest('.bisou-scene-vue__fermer'))) quitterVue();
    });
    /* Zoom puis déplacement, à l'ordinateur (Max, 26/10 : « pour les mockups et le bloc de gauche en général, quand on
       zoom on doit aussi ensuite pouvoir faire défiler » ; fiche a26-fiche-commandes-image) : un clic sur la scène la
       double, le point cliqué reste sous le curseur ; on s'y déplace en la faisant glisser, à la molette ou au pavé ; un
       clic sans glisser la rend entière ; la croix et Échap ferment toujours. Au téléphone, le doigt passe d'une vue à l'autre. */
    var souris = window.matchMedia('(min-width: 750px) and (pointer: fine)');
    var gl = null;
    boite.addEventListener('pointerdown', function (e) {
      gl = null;
      if (!souris.matches || !e.isPrimary || e.button !== 0) return;
      e.preventDefault();
      gl = { x: e.clientX, y: e.clientY, l: el.scrollLeft, t: el.scrollTop, bouge: false };
    });
    el.addEventListener('pointermove', function (e) {
      if (!gl || !el.classList.contains('is-zoom')) return;
      var dx = e.clientX - gl.x, dy = e.clientY - gl.y;
      if (!gl.bouge && Math.abs(dx) + Math.abs(dy) < 5) return;
      if (!gl.bouge) { gl.bouge = true; el.classList.add('is-glisse'); }
      el.scrollLeft = gl.l - dx;
      el.scrollTop = gl.t - dy;
    });
    el.addEventListener('pointerup', function (e) {
      var g = gl;
      gl = null;
      el.classList.remove('is-glisse');
      if (g && g.bouge) {
        /* le clic qui suit un glissé lâché sur le fond ne ferme pas la vue (à 3 440, la scène doublée laisse le fond
           visible de chaque côté) ; ce clic part dans la même tâche que le pointerup, le drapeau tombe juste après */
        apresGlisse = true;
        setTimeout(function () { apresGlisse = false; }, 0);
      }
      if (!g || g.bouge) return;
      if (el.classList.contains('is-zoom')) {
        el.classList.remove('is-zoom');
        boite.style.width = '';
        el.scrollLeft = 0; el.scrollTop = 0;
        return;
      }
      var r = boite.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var rx = (e.clientX - r.left) / r.width, ry = (e.clientY - r.top) / r.height;
      el.classList.add('is-zoom');
      boite.style.width = Math.round(r.width * 2) + 'px';
      /* une pièce plus fine pour la taille doublée, une fois prête (pas d'éclair) */
      if (tirage) nette(3600);
      var pieceZoom = tirage ? null : avecLargeur(scene.s.piece, 3600);
      if (pieceZoom) precharger(pieceZoom).then(function () { if (clone.piece.getAttribute('src') !== pieceZoom) clone.piece.setAttribute('src', pieceZoom); }, function () {});
      var r2 = boite.getBoundingClientRect();
      el.scrollLeft = Math.round(r2.left + el.scrollLeft + rx * r2.width - e.clientX);
      el.scrollTop = Math.round(r2.top + el.scrollTop + ry * r2.height - e.clientY);
    });
    document.documentElement.classList.add('bisou-scene-vue-ouverte');
    if (suite) el.classList.add('is-open', 'is-suite');
    /* la voisine arrive du bord, là où le doigt l'a tirée */
    if (depart) {
      /* au doigt, la nouvelle vue arrive entière, comme une page, collée à celle qui part ; celle-ci reste dessous, sans
         ses boutons, et glisse au même pas */
      [].forEach.call(depart.el.children, function (c) { if (c !== depart.vues) c.style.visibility = 'hidden'; });
      el.style.transform = 'translate3d(' + Math.round(arrivee * (window.innerWidth + ECART) + depart.x) + 'px, 0, 0)';
    }
    else if (arrivee && TEL.matches && !CALME.matches) decaler(arrivee * (window.innerWidth + ECART), 0);
    requestAnimationFrame(function () {
      el.classList.add('is-open');
      /* arrivée sans fondu, mais la fermeture garde le sien */
      el.classList.remove('is-suite');
      if (clone) clone.poser();
      /* au téléphone, la pièce déborde en largeur : on part centré sur le cadre */
      if (clone && boite.clientWidth > el.clientWidth + 1) {
        /* le cadre (les trois tailles : les trois) tient dans l'écran avec de l'air, sinon la pièce rapetisse et se
           tient au milieu de la hauteur : la pièce ne se parcourt plus du doigt, rien du cadre ne doit sortir */
        var bc = bordsCadres(scene), g = bc[0], dr = bc[1];
        var besoin = (dr - g) * boite.clientWidth + 2 * ECART;
        if (TEL.matches && dr > g && besoin > el.clientWidth) {
          var k = el.clientWidth / besoin;
          boite.style.width = Math.floor(boite.clientWidth * k) + 'px';
          boite.style.height = Math.floor(boite.clientHeight * k) + 'px';
        }
        var milieu = dr > g ? (g + dr) / 2 : 0.5;
        el.scrollLeft = Math.max(0, Math.round(milieu * boite.clientWidth - el.clientWidth / 2));
        /* la mention suit l'écran, pas la pièce qui se parcourt du doigt */
        var mention = boite.querySelector('.bisou-scene__mention');
        if (mention) { mention.style.margin = ''; el.appendChild(mention); }
      }
      if (vues.style.transform) { void vues.offsetWidth; decaler(0, 280); }
      if (depart) {
        var courbe = 'transform ' + DUREE_GLISSE + 'ms cubic-bezier(0.22, 1, 0.36, 1)';
        void el.offsetWidth;
        el.style.transition = courbe;
        el.style.transform = 'translate3d(0px, 0, 0)';
        depart.vues.style.transition = courbe;
        depart.vues.style.transform = 'translate3d(' + Math.round(-arrivee * (window.innerWidth + ECART)) + 'px, 0, 0)';
        /* tant que la vue glisse, ses boutons et sa mention (fixed) se placent dans la vue et suivent le défilement qui
           centre la pièce : on le leur rend, sinon ils arrivent 120 à 140 px à gauche et sautent à la fin (relecture) */
        var fixes = [].filter.call(el.children, function (c) { return c !== vues; });
        fixes.forEach(function (c) { c.style.translate = el.scrollLeft + 'px ' + el.scrollTop + 'px'; });
        setTimeout(function () {
          fixes.forEach(function (c) { c.style.translate = ''; });
          el.style.transition = '';
          el.style.transform = '';
          if (depart.el.parentNode) depart.el.parentNode.removeChild(depart.el);
        }, DUREE_GLISSE + 20);
      }
      try { fermer.focus({ preventScroll: true }); } catch (e) {}
    });
  }

  /* ── La galerie ── */
  /* Une diapo qui ne doit pas se montrer (scène sans données, pièce ou atlas absents) reste dans la liste,
     masquée : au changement de variante, le thème remet toute diapo du serveur absente de la liste (Dawn,
     updateMedia), une diapo retirée reviendrait donc vide. Masquée, elle n'est ni comptée ni atteinte
     (cercles, flèches, slider : ils ne voient que les diapos qui ont une largeur). */
  function masquer(li) {
    if (!li || li.hasAttribute('data-bisou-masquee')) return false;
    li.setAttribute('data-bisou-masquee', '');
    li.setAttribute('aria-hidden', 'true');
    li.hidden = true;
    li.style.setProperty('display', 'none', 'important');
    return true;
  }

  function lire(root, sel) {
    var n = root.querySelector(sel);
    if (!n) return null;
    try { return JSON.parse(n.textContent); } catch (e) { return null; }
  }

  function etatCourant() {
    var row = document.querySelector('.rv5-format-row.active[data-f]');
    var fl = document.getElementById('rv5-bloc-fmt');
    var fmt = row ? row.getAttribute('data-f') : (fl && fl.dataset.initFormat) || 'A4';
    var sw = document.querySelector('.rv5-color-swatch.active[data-col]');
    var col = sw ? sw.getAttribute('data-col') : 'none';
    return { format: fmt, withFrame: col !== 'none', color: col };
  }

  function init() {
    var root = document.querySelector('media-gallery');
    if (!root) return;
    var cfg = lire(root, 'script[data-bisou-scenes]');
    if (!cfg || !cfg.scenes || !cfg.scenes.length || !cfg.produit) return;
    var prod = cfg.produit;
    if (!prod.couleur) {
      var fl = document.getElementById('rv5-bloc-fmt');
      prod.couleur = fl && fl.dataset.initColor && fl.dataset.initColor !== 'none' ? fl.dataset.initColor : 'noir';
    }
    var scenes = [], parId = {};
    cfg.scenes.forEach(function (s) {
      if (!s || !s.id || !s.formats || !s.piece) return;
      var li = root.querySelector('li[data-bisou-scene="' + s.id + '"]');
      if (!li) return;
      var sc = s.tous ? new Trois(li, prod, s) : new Scene(li, prod, s);
      scenes.push(sc);
      parId[s.id] = sc;
    });
    /* La vue de profil d'une scène (Max, 28/10 : « une vue en plus qui soit une belle vue de profil avec des flous » ;
       fiche c28-fiche-vue-profil) : la même pièce vue de trois quarts, mise au point sur le cadre, où se pose le tirage
       de la fiche (s.profil, mêmes données qu'une scène). Elle ne vit que dans la vue en grand, juste après sa scène :
       pas de diapo dans la galerie, rien ne se charge avant qu'on l'ouvre. */
    scenes.forEach(function (sc) {
      var p = sc.s.profil;
      if (sc.parts || !p || !p.id || !p.formats || !p.piece) return;
      var li = document.createElement('li');
      li.innerHTML = '<div class="bisou-scene" role="img"><img class="bisou-scene__piece" alt="" decoding="async">' +
        '<span class="bisou-scene__ombre" aria-hidden="true"></span><img class="bisou-scene__cadre" alt="" decoding="async">' +
        '<span class="bisou-scene__lumiere" aria-hidden="true"></span></div>';
      li.firstChild.setAttribute('aria-label', sc.stage.getAttribute('aria-label') || '');
      sc.profil = new Scene(li, prod, p);
      sc.profil.parent = sc;
    });
    /* une diapo sans données (scène inconnue, orientation pas encore rendue) ne se montre pas */
    var orphelines = 0;
    Array.prototype.forEach.call(root.querySelectorAll('li[data-bisou-scene]'), function (li) {
      if (!parId[li.getAttribute('data-bisou-scene')] && masquer(li)) orphelines++;
    });
    if (orphelines) document.dispatchEvent(new CustomEvent('bisou:gallery-changed'));
    if (!scenes.length) return;

    /* Une scène dont la pièce ou l'atlas n'est pas (encore) dans Shopify › Fichiers est masquée tout de suite
       (rien d'autre ne se charge pour elle) : on les vérifie avec une vignette de quelques octets, sans attendre
       la vue principale. */
    scenes.forEach(function (sc) {
      var casse = function () {
        document.dispatchEvent(new CustomEvent('bisou:scene-broken', { detail: { id: sc.s.id } }));
      };
      precharger(avecLargeur(sc.s.piece, 32)).then(null, casse);
      if (sc.s.atlas && sc.s.atlas.url) precharger(avecLargeur(sc.s.atlas.url, 32)).then(null, casse);
    });

    var etat = etatCourant();
    scenes.forEach(function (sc) {
      sc.aller(etat.format, etat.withFrame ? etat.color : null);
      if (sc.profil) sc.profil.aller(etat.format, etat.withFrame ? etat.color : null);
    });

    /* Les images des scènes se chargent juste après la vue principale (jamais avant elle), ou dès que le
       visiteur touche la galerie. */
    var toutActive = false;
    var activerTout = function () { toutActive = true; scenes.forEach(function (sc) { sc.activer(); }); };

    /* Les diapos des scènes qui n'ont pas le format choisi se cachent, celles qui l'ont de nouveau reviennent : la
       galerie refait ses points, et la diapo regardée reste à l'écran (une diapo cachée ou rendue avant elle la
       décalerait d'un cran). Celle qu'on regardait et qui se cache : la galerie revient d'elle-même à la vue
       principale (bisou-product-gallery.js, changerait). */
    var suivreFormat = function (fmt) {
      var vu = null;
      if (liste) {
        var r0 = liste.getBoundingClientRect(), mid = r0.left + r0.width / 2;
        Array.prototype.forEach.call(liste.children, function (li) {
          var r = li.getBoundingClientRect();
          if (r.width && r.left <= mid && r.right >= mid) vu = li;
        });
      }
      var bouge = [];
      scenes.forEach(function (sc) { if (sc.horsFormat && sc.horsFormat(fmt)) bouge.push(sc); });
      if (!bouge.length) return;
      if (vue && vue.scene && vue.scene.li.hasAttribute('data-bisou-hors-format')) quitterVue();
      if (vu && liste && !vu.hidden && vu.getBoundingClientRect().width) {
        var d = vu.getBoundingClientRect().left - liste.getBoundingClientRect().left;
        if (Math.abs(d) > 1) {
          var sb = liste.style.scrollBehavior;
          liste.style.scrollBehavior = 'auto';
          liste.scrollLeft += d;
          liste.style.scrollBehavior = sb;
        }
      }
      if (toutActive) bouge.forEach(function (sc) { sc.activer(); });
      document.dispatchEvent(new CustomEvent('bisou:gallery-changed'));
    };
    var liste = root.querySelector('.product__media-list');
    suivreFormat(etat.format);
    var plusTard = function () {
      if ('requestIdleCallback' in window) window.requestIdleCallback(activerTout, { timeout: 1200 });
      else setTimeout(activerTout, 300);
    };
    var principale = root.querySelector('.product__media-item:not(.bisou-scene-slide) img');
    if (!principale || (principale.complete && principale.naturalWidth)) plusTard();
    else {
      principale.addEventListener('load', plusTard, { once: true });
      principale.addEventListener('error', plusTard, { once: true });
    }
    setTimeout(activerTout, 2500);
    if (liste) liste.addEventListener('scroll', activerTout, { passive: true, once: true });
    root.addEventListener('pointerenter', activerTout, { once: true });
    root.addEventListener('touchstart', activerTout, { passive: true, once: true });
    root.addEventListener('focusin', activerTout, { once: true });

    document.addEventListener('bisou:variant-change', function (e) {
      var d = e.detail || {};
      if (!d.format) return;
      var voulue = d.withFrame && d.color && d.color !== 'none' ? d.color : null;
      scenes.forEach(function (sc) {
        sc.aller(d.format, voulue);
        if (sc.profil) sc.profil.aller(d.format, voulue);
      });
      /* après les autres écouteurs : la galerie juge d'abord la diapo regardée telle qu'elle est */
      setTimeout(function () { suivreFormat(d.format); }, 0);
    });

    /* Une scène dont la pièce ou l'atlas manque ne se montre plus : jamais de diapo cassée. */
    document.addEventListener('bisou:scene-broken', function (e) {
      var sc = parId[e.detail && e.detail.id];
      if (!sc || !masquer(sc.li)) return;
      document.dispatchEvent(new CustomEvent('bisou:gallery-changed'));
    });

    /* « Agrandir » et le clic sur une scène ouvrent la scène en grand, pas la vue principale. */
    var sceneVisible = function () {
      if (!liste) return null;
      var r0 = liste.getBoundingClientRect();
      var mid = r0.left + r0.width / 2;
      for (var i = 0; i < scenes.length; i++) {
        var r = scenes[i].li.getBoundingClientRect();
        if (r.width && r.left <= mid && r.right >= mid) return scenes[i];
      }
      return null;
    };
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var exp = e.target.closest('[data-rv5-expand]');
      var surScene = e.target.closest('.bisou-scene-slide .bisou-scene');
      /* le tirage seul s'ouvre dans la même vue que les scènes, plus dans la loupe de Dawn (Max, 28/10 : « quand on fait
         expand, ça vient juste zoomer dans l'image ») : le clic sur sa diapo (le calque de Dawn) et « Agrandir » */
      var surTirage = liste && liste.contains(e.target) && !e.target.closest('.bisou-scene-slide') ? e.target.closest('.product__media-item') : null;
      if (!exp && !surScene && !surTirage) return;
      var sc = surScene ? parId[surScene.closest('[data-bisou-scene]').getAttribute('data-bisou-scene')] : (surTirage ? entreeTirage(surTirage) : sceneVisible());
      if (!sc && exp && liste) {
        var r0 = liste.getBoundingClientRect(), mid = r0.left + r0.width / 2;
        var vis = [].slice.call(liste.children).filter(function (li) { var r = li.getBoundingClientRect(); return r.width && r.left <= mid && r.right >= mid && !li.hasAttribute('data-bisou-scene'); })[0];
        if (vis && vis.querySelector('img')) sc = entreeTirage(vis);
      }
      if (!sc || (!sc.tirage && !sc.actif)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      ouvrirVue(sc, exp || surScene);
    }, true);

    var ouvrable = function (x) { return x.actif && x.fmt && !x.li.hasAttribute('data-bisou-masquee') && !x.li.hasAttribute('data-bisou-hors-format'); };
    /* la vue de profil suit sa scène, si elle a le format choisi et que sa pièce n'a pas manqué */
    var avecProfil = function (sc) {
      var p = sc.profil;
      return p && !p.li.classList.contains('is-broken') && sc.fmt && p.s.formats[sc.fmt] ? [sc, p] : [sc];
    };
    var entreeTirage = function (li) { return li.__bisouVue || (li.__bisouVue = { tirage: true, li: li }); };
    /* dans l'ordre de la galerie : le tirage seul (et toute diapo photo qui se voit), puis les scènes, chacune suivie
       de sa vue de profil */
    var ouvrables = function () {
      var l = [];
      if (!liste) scenes.filter(ouvrable).forEach(function (sc) { l.push.apply(l, avecProfil(sc)); });
      else [].slice.call(liste.children).forEach(function (li) {
        var id = li.getAttribute('data-bisou-scene');
        if (id) { var sc = parId[id]; if (sc && ouvrable(sc)) l.push.apply(l, avecProfil(sc)); }
        else if (li.querySelector('img') && li.getBoundingClientRect().width) l.push(entreeTirage(li));
      });
      return l;
    };
    rang = function (sc) { var l = ouvrables(); return { i: l.indexOf(sc), n: l.length }; };
    prechauffer = function () { scenes.forEach(function (sc) { if (sc.profil && !sc.profil.actif) sc.profil.activer(); }); };
    voisines = function (sc) { var l = ouvrables(), i = l.indexOf(sc); return i < 0 ? [] : [l[i + 1], l[i - 1]].filter(Boolean); };
    voisine = function (sc, pas) {
      var l = ouvrables();
      var i = l.indexOf(sc);
      var v = i < 0 ? null : l[i + pas] || null;
      if (v && liste) {
        /* une vue de profil n'a pas de diapo : la galerie se cale sur sa scène */
        var d = (v.parent || v).li.getBoundingClientRect().left - liste.getBoundingClientRect().left;
        var sb = liste.style.scrollBehavior;
        liste.style.scrollBehavior = 'auto';
        liste.scrollLeft += d;
        liste.style.scrollBehavior = sb;
      }
      return v;
    };
    /* une entrée de la vue restée en tête de l'historique (page rechargée vue ouverte) ne sert plus */
    if (dansHistoire()) { try { history.replaceState(etatSans(false), ''); } catch (e) {} }

    window.BisouScenes = {
      scenes: scenes,
      config: cfg,
      visible: function () { var s = sceneVisible(); return s ? s.s.id : null; },
      changerait: function (id, fmt, voulue) { var sc = parId[id]; return sc ? sc.changerait(fmt, voulue) : false; },
      ouvrir: function (id) { ouvrirVue(parId[id]); },
      fermer: quitterVue,
      activer: activerTout
    };
    document.dispatchEvent(new CustomEvent('bisou:scenes-ready'));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
